import { useEffect, useRef, useState } from "react";
import { useLanguage } from "../contexts/LanguageContext";
import {
  loadGoogleMaps,
  onGoogleMapsAuthFailure,
  isGoogleMapsAuthFailure,
} from "../services/googleMapsLoader";

// Uses the new google.maps.places.PlaceAutocompleteElement (the legacy
// places.Autocomplete is deprecated and unavailable to newer API projects).
// No `types` restriction, so businesses/POIs (malls, shops) are returned too —
// the old ["address"] filter hid results like "Viaport Asia Outlet Shopping".
export default function AddressAutocomplete({
  onSelectAddress,
  initialValue = "",
  // Owns retry. The parent has to remount BOTH this and the sibling MapPicker —
  // the map hid itself on the same failed load and its init effect won't re-run
  // on its own — so retrying locally here would only ever fix half the form.
  // No handler, no retry button.
  onRetry,
}) {
  const { t } = useLanguage();
  const containerRef = useRef(null);
  const [unavailable, setUnavailable] = useState(false);
  // A rejected API key is a misconfiguration on our side, not something the
  // merchant can clear by turning off a blocker — so it gets its own message and
  // no retry button.
  const [authFailed, setAuthFailed] = useState(false);

  // Keep the latest callback without re-initialising the element.
  const onSelectRef = useRef(onSelectAddress);
  onSelectRef.current = onSelectAddress;

  useEffect(() => {
    let cancelled = false;
    let element = null;
    let handler = null;

    // Only runs once loadGoogleMaps() has resolved, which means `places` is
    // imported and the container is mounted — so no readiness check here. If
    // either assumption ever breaks, throwing lands in the .catch below and the
    // merchant gets the unavailable notice, rather than a silent empty field.
    const init = () => {
      if (cancelled || element) return;

      element = new google.maps.places.PlaceAutocompleteElement();
      element.style.width = "100%";
      // Best-effort: show the current address (edit) or the search hint.
      element.setAttribute(
        "placeholder",
        initialValue || t("settings.branches.form.searchPlaceholder"),
      );

      handler = async (event) => {
        const prediction = event.placePrediction;
        if (!prediction) return;
        const place = prediction.toPlace();
        await place.fetchFields({ fields: ["formattedAddress", "location"] });
        if (!place.location) return;

        onSelectRef.current({
          address: place.formattedAddress,
          lat: place.location.lat(),
          lng: place.location.lng(),
        });
      };

      element.addEventListener("gmp-select", handler);
      containerRef.current.appendChild(element);
    };

    // Maps is loaded on demand (see services/googleMapsLoader) rather than from
    // index.html, so the search box appears once the API is ready. When the load
    // fails, MapPicker hides itself and this component owns the message — the
    // two share one memoised promise, so they always fail together and a notice
    // in each would just be the same warning twice.
    setUnavailable(false);
    setAuthFailed(isGoogleMapsAuthFailure());
    loadGoogleMaps()
      .then(init)
      .catch((error) => {
        if (cancelled) return;
        console.warn("Address autocomplete unavailable:", error.message);
        setAuthFailed(isGoogleMapsAuthFailure());
        setUnavailable(true);
      });

    // Auth is only checked once something renders, so this can arrive after the
    // load resolved and the search box is already on screen.
    const unsubscribe = onGoogleMapsAuthFailure(() => {
      if (cancelled) return;
      setAuthFailed(true);
      setUnavailable(true);
    });

    return () => {
      cancelled = true;
      unsubscribe();
      if (element && handler) {
        element.removeEventListener("gmp-select", handler);
      }
      if (element?.parentNode) {
        element.parentNode.removeChild(element);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Without Maps there is no address search and no map pin, so the branch form
  // cannot capture a location at all. Say so, and let the merchant retry once
  // they have dealt with the blocker rather than reloading the dashboard.
  //
  // The keys matter. Both branches return a plain <div>, so without them React
  // reconciles the two as the SAME node — it just swaps className and adds the
  // message, leaving the Google element we appended imperatively (and which React
  // does not track) sitting inside the error box as a dead search field. Distinct
  // keys force a real unmount, which takes its children with it.
  if (unavailable) {
    return (
      <div
        key="maps-unavailable"
        className="rounded-md border border-amber-300 bg-amber-50 p-3"
      >
        <p className="text-sm text-amber-800">
          {t(
            authFailed
              ? "settings.branches.form.mapsAuthError"
              : "settings.branches.form.mapsUnavailable",
          )}
        </p>
        {!authFailed && onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="mt-2 text-sm font-medium text-amber-900 underline hover:no-underline"
          >
            {t("settings.branches.form.mapsRetry")}
          </button>
        )}
      </div>
    );
  }

  return <div key="maps-field" ref={containerRef} className="w-full" />;
}
